/**
 * web/src/screens/Notebook/VisibilityPill.tsx — the audience pill on a card
 * face (card vncVrGk1hc9i: moved out of index.tsx unchanged).
 */
import { useEffect, useRef, useState } from 'react';

/**
 * Visibility pill (card quWhYyrNiIbt, PRD K3) — one control on the card face
 * that reads the card's audience ("Shared with <partner>" / "Just you") and,
 * on click, opens a small menu to change it. It REPLACES the old
 * "Make just me" / "Share with partner" action pair: the pill states the
 * current truth instead of offering whichever single flip happened to be
 * missing. Owner-only (same `canRemove` gate the pair used) and the flip still
 * routes through the existing confirm + owner-only PUT, so nothing about the
 * server contract changes — only the affordance.
 */
export function VisibilityPill({ shared, mine, partnerName, busy, onSet }: {
  shared: boolean;
  mine: boolean;
  partnerName: string;
  busy: boolean;
  onSet: (next: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Close on outside press / Escape so the menu never traps the card.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  /* Whose eyes (Jonathan 2026-10-30): the pill used to say "Shared with
     <partner>" from whoever added the card, so a card the PARTNER shared read
     as "shared with partner" to the person it was shared with — a label about
     somebody else. From the viewer's side the fact is "this is shared with
     you"; only the owner is sharing it with a named person. */
  const label = !shared ? 'Just you' : mine ? `Shared with ${partnerName}` : 'Shared with you';
  return (
    <div className="nb-vis-chip" ref={ref}>
      <button
        type="button"
        className={`nb-vis-chip-btn${shared ? ' is-shared' : ' is-private'}${mine ? '' : ' is-readonly'}`}
        data-test="nb-visibility"
        aria-haspopup={mine ? 'menu' : undefined}
        aria-expanded={mine ? open : undefined}
        aria-label={mine ? `Who can see this — ${label}. Change it.` : `Who can see this — ${label}.`}
        disabled={busy || !mine}
        onClick={() => setOpen(o => !o)}
      >
        <span className="nb-vis-dot" aria-hidden="true" />
        {label}
      </button>
      {open && (
        <div className="nb-vis-menu" role="menu" aria-label="Who can see this">
          <button
            type="button"
            role="menuitemradio"
            aria-checked={shared}
            className={`nb-vis-opt${shared ? ' is-on' : ''}`}
            data-test="nb-vis-shared"
            onClick={() => { setOpen(false); if (!shared) onSet(true); }}
          >
            Shared with {partnerName}
          </button>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={!shared}
            className={`nb-vis-opt${!shared ? ' is-on' : ''}`}
            data-test="nb-vis-private"
            onClick={() => { setOpen(false); if (shared) onSet(false); }}
          >
            Just you
          </button>
        </div>
      )}
    </div>
  );
}
